import { useAuiState } from '@assistant-ui/react';

import { isAwaitingInputPart } from '#plugin-chat/hooks/isAwaitingInputPart.ts';
import { isPartStatusCancelled, isPartStatusError } from '#plugin-chat/hooks/isErroredToolPart.ts';
import { isFailureResult } from '#plugin-chat/lib/tool/result-envelope.ts';

export function usePartStatus() {
  // Tool-call parts carry a separate `isError` flag (assistant-ui core message
  // type), distinct from the part's status. JSON-parse failures and SDK-level
  // tool-input validation errors surface here with `status.type === 'complete'`,
  // so we MUST check `isError` independently — otherwise an errored tool call
  // renders as "Complete ✓" with empty content.
  const partIsError = useAuiState((state) =>
    state.part.type === 'tool-call' ? state.part.isError === true || isPartStatusError(state.part.status) : false,
  );

  // Detect interrupt state: the AISDKMessageConverter sets
  // status.type='requires-action' / reason='interrupt' on tool-call parts
  // when toolStatuses indicates an interrupt, but the MessageRuntime may
  // populate the `interrupt` field instead depending on the code path.
  // `isAwaitingInputPart` is the SSOT for that condition. `interrupt` is
  // already tool-call-gated above, so passing `type: 'tool-call'` keeps the
  // predicate's interrupt branch equivalent to the original `!!interrupt`
  // without an extra `state.part.type` selector.
  const isAwaitingInput = useAuiState((state) => isAwaitingInputPart(state.part));

  // A thrown `execute` that `<Tool>`'s `runToolCall` boundary caught settles as a
  // success-shaped part (`status.type === 'complete'`, `isError` unset) whose
  // failure marker lives inside the output envelope. `isFailureResult` is
  // the single source of truth for that discrimination — shared with
  // `isErroredToolPart`, `tool-identity` and `bindingProjection` so the failure
  // classification cannot drift between the status hook and the grouping.
  //
  // Selected as a boolean on purpose: `part.args`/`part.result` get a fresh
  // identity on every streamed token, so subscribing to the objects (e.g. via
  // useToolPart) re-renders every status consumer per token. The boolean only
  // changes when the failure classification actually flips.
  const isErrorResponse = useAuiState((state) => state.part.type === 'tool-call' && isFailureResult(state.part.result));

  const isError = partIsError || isErrorResponse;
  const isRunning = useAuiState((state) => state.part.status?.type === 'running');
  const isComplete = useAuiState((state) => state.part.status?.type === 'complete');
  const isCancelled = useAuiState((state) => isPartStatusCancelled(state.part.status));

  return {
    isRunning: isRunning && !isAwaitingInput,
    // A part with `isError: true` should NOT report as complete, even if the
    // status type is "complete" — downstream UIs (e.g. dashboard renderers)
    // would otherwise paint stale or empty content over an error.
    isComplete: isComplete && !isError,
    isCancelled,
    isError,
    isAwaitingInput,
  };
}
/**
 * The tool-display chrome's derived status union. Exactly one kind is
 * active per render; the render-map on `<Tool render>` is keyed by it.
 *
 * Derived from {@link usePartStatus} (the SSOT for the underlying
 * assistant-ui status/interrupt/failure-envelope discrimination) rather
 * than the raw assistant-ui `status.type` — a failure envelope arrives
 * with status `complete`, interrupts may never flip to
 * `requires-action`, and cancelled vs errored both live under
 * `incomplete`.
 */
export type ToolStatusKind = 'running' | 'awaitingInput' | 'complete' | 'error' | 'cancelled';

/**
 * Resolve the single active {@link ToolStatusKind} for the current
 * tool-call part. Precedence: awaitingInput > cancelled > error >
 * complete; anything else (including args still streaming before the
 * status settles) reads as `running`.
 */
export function useToolStatusKind(): ToolStatusKind {
  const { isComplete, isCancelled, isError, isAwaitingInput } = usePartStatus();
  if (isAwaitingInput) {
    return 'awaitingInput';
  }
  if (isCancelled) {
    return 'cancelled';
  }
  if (isError) {
    return 'error';
  }
  if (isComplete) {
    return 'complete';
  }
  return 'running';
}
